import type {
  SiteContent,
  GlobalSettings,
  PlansHeroContent,
  PlansGridContent,
  PlansCTAContent,
  FinancingHeroContent,
  FinancingOverviewContent,
  FinancingHowItWorksContent,
  FinancingPlansContent,
  FinancingBenefitsContent,
  ServiceLocationHeroContent,
  ServiceLocationMapContent,
  ContactHeroContent,
  ContactInfoContent,
  SEOGlobalContent,
  SEOPageContent,
} from '../types/cms';

export const GOOGLE_RATING = '5.0';

export const defaultGlobalSettings: GlobalSettings = {
  phone: '(903) 321-2265',
  email: 'help@bestneighborhomeservices.com',
  bookingUrl: 'https://book.housecallpro.com/book/Best-Neighbor-Home-Services/8f678fe5eaeb44e3ad165c3c9752c12b?v2=true',
  googleReviewsUrl: 'https://www.google.com/search?q=Best+Neighbor+Home+Services+reviews',
  license: 'TACLA140059C',
};

export const defaultContent: SiteContent = {
  hero: {
    heading: 'Your Trusted Home Service Partner',
    description: 'Veteran-owned and operated. We bring military-grade precision and neighborly care to every heating and cooling service.',
    bookButtonLabel: 'Book a Service Call',
    whyChooseUsButtonLabel: 'Why Choose Us',
    veteranBadgeText: 'Proudly Veteran Owned',
    veteranTagline: 'Serving those who served us',
    ratingText: `${GOOGLE_RATING} on Google`,
    locations: ['North Texas', 'Dallas-Fort Worth', 'Collin County', 'Denton County', 'Rockwall County'],
    stats: {
      jobsCompleted: '500+',
      satisfaction: '99%',
      citiesServed: '37',
      googleRating: GOOGLE_RATING,
    },
    whyChooseUsItems: [
      { title: 'Veteran-Owned Excellence', description: 'We bring military values of discipline, integrity, and accountability to every service call.' },
      { title: 'Fast Response Times', description: 'Same-day service and 24/7 emergency availability throughout North Texas.' },
      { title: 'Transparent Pricing', description: 'No hidden fees, no surprises. You will know the cost before we start work.' },
      { title: 'Lifetime Guarantees', description: '10-year parts and labor on installations, lifetime on repairs.' },
      { title: 'Advanced Diagnostics', description: 'Smart diagnostics technology ensures precision results every time.' },
      { title: 'Community Commitment', description: 'Locally owned and operated. We are your neighbors in North Texas.' },
    ],
    serviceItems: [
      { title: 'AC Installation & Repair', shortTitle: 'AC Service' },
      { title: 'Heating Services', shortTitle: 'Heating' },
      { title: 'Heat Pump Solutions', shortTitle: 'Heat Pumps' },
      { title: 'Mini Split Systems', shortTitle: 'Mini Splits' },
      { title: 'Preventive Maintenance', shortTitle: 'Maintenance' },
      { title: 'Emergency HVAC Service', shortTitle: 'Emergency' },
    ],
  },
  about: {
    sectionLabel: 'About Us',
    heading: 'Your Neighbors in Comfort, Safety & Peace of Mind',
    description: 'Best Neighbor Home Services was founded on a simple principle: treat every customer the way you would want to be treated. As a veteran-owned HVAC company based in McKinney, Texas, we bring military-grade precision and genuine neighborly care to every service call throughout North Texas.',
    buttonLabel: 'Get to Know Us',
    coreValuesHeading: 'Our Core Values',
    rating: GOOGLE_RATING,
    features: [
      'Advanced smart diagnostics technology',
      'Lifetime guarantees on our work',
      'Emergency repairs available 24/7',
    ],
    coreValues: [
      { title: 'Military Excellence', description: 'Discipline, integrity, and attention to detail in every HVAC job.' },
      { title: 'Neighborly Service', description: 'We treat every customer like a neighbor and every home like our own.' },
      { title: 'Reliable Response', description: '24/7 availability with fast response times when you need help.' },
      { title: 'Community Focused', description: 'Locally owned and invested in the North Texas community.' },
    ],
    credentials: [
      { title: 'Licensed & Insured', subtitle: 'TACLA140059C' },
      { title: 'Veteran Owned', subtitle: 'Expert Technicians' },
    ],
  },
  services: {
    sectionLabel: 'Our Services',
    heading: 'Professional Heating & Cooling Solutions',
    description: 'From routine maintenance to emergency repairs, we deliver expert HVAC services backed by lifetime guarantees and transparent pricing.',
    guidanceLabel: 'Expert Guidance',
    guidanceHeading: 'Not Sure Which Service You Need?',
    guidanceDescription: "Our expert technicians will assess your home's specific needs and recommend the best solution. Free estimates with no obligation, ever.",
    estimateButtonLabel: 'Get Your Free Estimate',
    services: [
      {
        title: 'AC Installation & Repair',
        shortTitle: 'AC Service',
        description: 'Professional air conditioning installation and repair services with same-day availability and advanced smart diagnostics for precision results.',
        features: ['Same-Day Service Available', 'Lifetime Repair Guarantees', '10-Year Parts & Labor Warranty'],
      },
      {
        title: 'Heating Services',
        shortTitle: 'Heating',
        description: 'Complete heating system solutions from furnace repair and installation to seasonal tune-ups. Keep your family warm through every North Texas winter.',
        features: ['Furnace Repair & Installation', 'Efficiency Upgrades', 'Seasonal Maintenance Plans'],
      },
      {
        title: 'Heat Pump Solutions',
        shortTitle: 'Heat Pumps',
        description: 'Energy-efficient heat pump systems that provide year-round comfort. One system for both heating and cooling with significant energy savings.',
        features: ['Dual Heating & Cooling', 'Lower Energy Bills', 'Environmentally Friendly'],
      },
      {
        title: 'Mini Split Systems',
        shortTitle: 'Mini Splits',
        description: 'Ductless mini split installations perfect for room additions, garages, converted spaces, and homes without existing ductwork.',
        features: ['Zone-by-Zone Comfort', 'No Ductwork Required', 'Whisper-Quiet Operation'],
      },
      {
        title: 'Preventive Maintenance',
        shortTitle: 'Maintenance',
        description: 'Prevention Plan memberships starting at just $10.99/month. Priority scheduling, quarterly filter delivery, and comfort credits included.',
        features: ['Priority Scheduling', 'Quarterly Filter Delivery', 'Comfort Credits Included'],
      },
      {
        title: 'Emergency HVAC Service',
        shortTitle: 'Emergency',
        description: 'When your system fails, we respond fast. 24/7 emergency repairs throughout North Texas with no overtime charges or hidden fees.',
        features: ['24/7 Availability', 'No Overtime Fees', 'Fast Response Times'],
      },
    ],
  },
  testimonials: {
    sectionLabel: 'Testimonials',
    heading: 'What Our Neighbors Are Saying',
    description: 'Real stories from real families throughout North Texas. We are proud to serve our community with integrity and excellence.',
    googleLinkText: 'See All Reviews on Google',
    testimonials: [
      { name: 'The Moriel Family', location: 'North Texas', rating: 5, text: 'Was AWESOME! Upfront and honest after an inspection of our AC unit. Never made it about him, but used his experience to give us the best option for us, for comfortability but also affordability. His team were exceptional - we were given an 8 hr window to complete install and they had it done in 4-5 hrs tops.' },
      { name: 'Fred Haight', location: 'Melissa & Plano', rating: 5, text: 'Honest, hard working, and frugal. He has great service at a fair price. I have 2 Dental Offices and many AC/furnace units on our family property. I highly recommend Best Neighbor Home Services for all your HVAC needs.' },
      { name: 'Kyoung Min Lee', location: 'Allen', rating: 5, text: 'I had a 27 years old A/C replaced. He was sticking to the basics of how the installation should be done for the best efficiency through the end. He was very courteous and honest too. He fixed a startup issue of another A/C that others could not solve.' },
      { name: 'Chatrapathy Velagandula', location: 'North Texas', rating: 5, text: 'Jacob from Best Neighbor has serviced our AC units. He is on time, very patient and explained the entire process and the condition of our HVAC and the thermostat very clearly. Very happy with the preventative service provided by Jacob and the ease of scheduling with the Best Neighbor' },
      { name: 'Nick Schoeneberger', location: 'North Texas', rating: 5, text: 'Excellent work at a fair price. Always glad to give my business to Jake and the Best Neighbor team.' },
      { name: 'Tracy', location: 'North Texas', rating: 5, text: 'Best Neighbor was able to quickly and honestly resolve the HVAC issues needed for the sale, inspection request. His knowledge was superior to the other HVAC quotes and saved me hundreds of dollars!!!! Thank you!!!!!!' },
      { name: 'Ixchel De La Fuente', location: 'North Texas', rating: 5, text: "Great service and very fast, didn't have to wait long for someone to come fix my ac. Would definitely call again whenever I need help with my ac" },
      { name: 'Amy Lowe', location: 'North Texas', rating: 5, text: 'Jacob has worked hard to first extend the life of our old system and now to get our new system balanced and running efficiently. We have been happy with our service.' },
    ],
  },
  cta: {
    sectionLabel: 'Get Started',
    heading: 'Do Not Wait Until It Breaks',
    description: 'Schedule your free estimate today and discover why North Texas families trust Best Neighbor Home Services for all their heating and cooling needs.',
    sameDayLabel: 'Same-Day Service Available',
    subheading: 'Ready to Get Started?',
    buttonLabel: 'Book Your Free Estimate',
    callText: 'or call us directly',
    tagline: 'No obligation. No pressure. Just honest service.',
    benefits: ['Available 24/7', 'No Service Fees', 'Free Estimates', 'Lifetime Guarantees'],
  },
  serviceAreas: {
    sectionLabel: 'Service Coverage',
    heading: 'Areas We Serve',
    description: 'Providing 24/7 emergency HVAC services across North Texas - from Dallas to Sherman and everywhere in between.',
    notFoundHeading: 'Do Not See Your City on the Map?',
    notFoundDescription: 'Our coverage extends beyond what is shown here. We serve many additional communities throughout North Texas. Give us a call to confirm service in your area.',
    counties: [
      { name: 'Collin County', color: '#D97706', cities: ['McKinney', 'Allen', 'Frisco', 'Plano', 'Celina', 'Anna', 'Princeton', 'Fairview', 'Lucas', 'Parker', 'Murphy', 'Wylie', 'Richardson', 'Melissa'] },
      { name: 'Grayson County', color: '#1B3A4B', cities: ['Sherman', 'Denison', 'Whitesboro', 'Van Alstyne', 'Howe', 'Gunter'] },
      { name: 'Hunt County', color: '#92734E', cities: ['Greenville', 'Commerce', 'Quinlan', 'Caddo Mills', 'West Tawakoni', 'Campbell', 'Celeste', 'Wolfe City'] },
      { name: 'Dallas Area', color: '#374151', cities: ['Dallas', 'Addison', 'Carrollton', 'Farmers Branch', 'Coppell', 'The Colony', 'Little Elm', 'Sachse', 'Mesquite'] },
    ],
  },
  whyChooseUs: {
    sectionLabel: 'Why Choose Us',
    heading: 'The Best Neighbor Difference',
    description: 'Choosing an HVAC partner is about more than technical expertise. It is about finding someone who treats your home with care and respect. Here is why North Texas families choose Best Neighbor Home Services.',
    quote: 'You shall love the Lord your God with all your heart, with all your soul, with all your strength, and with all your mind, and your neighbor as yourself.',
    quoteLabel: 'Our Guiding Principle',
    veteranHeading: 'Veteran Owned Business',
    veteranDescription: 'Proudly serving North Texas with the discipline and integrity learned through military service.',
    reasons: [
      { title: 'Veteran-Owned Excellence', description: 'We bring military values of discipline, integrity, and accountability to every service call. Your home is treated like our own.' },
      { title: 'Fast Response Times', description: 'Our local presence in McKinney means quick dispatch throughout North Texas. Same-day service and 24/7 emergency availability.' },
      { title: 'Transparent Pricing', description: 'No hidden fees, no surprises. You will know the cost before we start work. We believe in honest, upfront pricing.' },
      { title: 'Lifetime Guarantees', description: 'We stand behind our work with industry-leading warranties. 10-year parts and labor on installations, lifetime on repairs.' },
      { title: 'Advanced Diagnostics', description: 'Smart diagnostics technology ensures precision results. We identify problems accurately and fix them right the first time.' },
      { title: 'Community Commitment', description: 'Locally owned and operated. We are your neighbors in North Texas, invested in the communities we serve.' },
    ],
  },
  whatWeHandle: {
    sectionLabel: 'Emergency Service',
    heading: "HVAC Emergency? We're On Our Way.",
    description: 'When your system fails, every minute matters. Our team responds fast with no overtime charges, no hidden fees -- just honest, urgent service.',
    whatWeHandleHeading: 'What We Handle',
    disclaimer: "Not sure if it's an emergency? Call us anyway. We'll walk you through it over the phone and send a tech if needed -- no charge for the call.",
    scheduleButtonLabel: 'Schedule Online',
    stats: {
      responseTime: '<60',
      availability: '24/7',
      overtimeFees: '$0',
    },
    emergencyItems: [
      { title: 'Complete System Failures', description: "No air flow, system won't turn on, or total breakdown during extreme weather." },
      { title: 'Refrigerant Leaks', description: 'Ice buildup on lines, hissing sounds, or declining cooling performance.' },
      { title: 'Electrical Issues', description: 'Tripped breakers, burnt wiring smells, or compressor not engaging.' },
      { title: 'Strange Noises & Odors', description: 'Grinding, squealing, banging, or burning smells from your unit.' },
      { title: 'Thermostat Malfunctions', description: 'System unresponsive, running nonstop, or not cycling properly.' },
      { title: 'Frozen Coils & Heat Exchangers', description: 'Ice on your unit or cracked heat exchangers requiring immediate attention.' },
    ],
  },
  trustedPartners: {
    sectionLabel: 'Our Partners',
    heading: 'Trusted Brands We Install & Service',
    description: 'We partner with the most trusted names in HVAC, ensuring every system we install delivers reliability, efficiency, and lasting comfort for your North Texas home.',
    certifiedHeading: 'Certified & Licensed',
    consultationButtonLabel: 'Request a Consultation',
    brands: [
      { name: 'Daikin', highlight: 'Premium Efficiency', description: 'Industry-leading energy efficiency and innovative inverter technology built for the North Texas climate.' },
      { name: 'Trane', highlight: 'Built to Last', description: 'Legendary reliability with a proven track record of dependable heating and cooling performance.' },
      { name: 'Samsung', highlight: 'Smart Technology', description: 'Cutting-edge climate systems with smart home integrations and whisper-quiet operation.' },
      { name: 'Goodman', highlight: 'Value Leader', description: 'Trusted residential HVAC systems that deliver dependable comfort at an exceptional value.' },
      { name: 'Lennox', highlight: 'Precision Comfort', description: 'Premium air quality and precise temperature control with industry-best efficiency ratings.' },
      { name: 'Carrier', highlight: 'Innovation Pioneer', description: 'Over a century of HVAC innovation delivering advanced comfort solutions for every home.' },
    ],
    certifications: ['Licensed TACLA140059C', 'Fully Insured', 'Factory Authorized Dealer'],
  },
  financingTeaser: {
    sectionLabel: 'Financing Available',
    heading: 'Quality Comfort Within Your Budget',
    description: "Don't let unexpected HVAC costs catch you off guard. We partner with OPTIMUS and Wells Fargo to offer flexible financing that makes comfort affordable.",
    buttonLabel: 'View Financing Options',
    highlights: [
      '0% APR for 12-72 Months',
      'Over 90% Approval Rate',
      'No Down Payment Required',
      'Quick Credit Decision',
      'Terms up to 180 Months',
      'Projects Over $3,500',
    ],
  },
  faq: {
    sectionLabel: 'FAQ',
    heading: 'Questions? We Have Answers',
    description: 'Get answers to common questions about HVAC installation, repair, maintenance, and financing in North Texas. Can not find what you are looking for? Reach out directly.',
    stillHaveQuestionsHeading: 'Still Have Questions?',
    stillHaveQuestionsText: 'Our friendly team is here to help with any questions about your HVAC needs. No obligation, no pressure.',
    faqs: [
      { question: 'How much does a new HVAC system cost in North Texas?', answer: 'New HVAC system installations in North Texas typically range from $7,500 to $15,000+ depending on your home size, existing ductwork condition, and system tier. Our Thrifty Essential System starts at just $7,500 or $99/month with 0% APR financing for up to 72 months. We offer free, no-obligation estimates with transparent, upfront pricing.' },
      { question: 'Do you offer same-day HVAC service?', answer: 'Yes! We offer same-day HVAC service throughout McKinney, Allen, Frisco, Plano, Sherman, Greenville, and all surrounding communities. Our 24/7 emergency service (seven days a week, 24 hours a day) means you are never far from expert help when your heating or cooling system fails.' },
      { question: 'What HVAC brands do you install and service?', answer: "We are authorized dealers for three of the industry's most trusted HVAC brands: Daikin, Trane, and Samsung. These manufacturers are known for reliability, energy efficiency, and long-term performance in the North Texas climate. Our certified technicians are factory-trained on these systems." },
      { question: 'Are you licensed to work in Texas?', answer: 'Yes, Best Neighbor Home Services is fully licensed (TACLA140059C) and insured to perform HVAC installation, repair, and maintenance throughout Texas. As a veteran-owned business, we bring military-grade precision, integrity, and accountability to every service call.' },
      { question: 'What is included in your Prevention Plan membership?', answer: 'Our Prevention Plan memberships start at just $10.99/month and include priority service, seasonal tune-ups, quarterly filter changes delivered to your door, comprehensive system diagnostics, exclusive member discounts, zero after-hours fees for emergency service, and Comfort Credits that roll forward toward future upgrades.' },
      { question: 'Do you offer financing for HVAC installations?', answer: 'Yes! We partner with OPTIMUS and Wells Fargo to offer flexible financing options including 0% APR for 12-72 months on new HVAC projects over $3,500, low monthly payments with 7.9% APR for extended terms up to 180 months, and over 90% approval rates. No down payment required, and most applications receive same-day decisions.' },
      { question: 'What makes Best Neighbor different from other HVAC companies?', answer: 'As a veteran-owned HVAC company, we bring military values of discipline, integrity, and service excellence to every job. We are locally owned in McKinney with fast response times. Our technicians use advanced smart diagnostics, we offer lifetime guarantees on workmanship, and Prevention Plan members enjoy zero after-hours fees.' },
    ],
  },
  plansHero: {
    title: 'HVAC',
    titleAccent: 'Membership',
    titleSuffix: 'Plans',
    description: 'Exclusive HVAC Membership Plans Designed to Keep North Texas Homes Comfortable and Efficient Year-Round',
  } as PlansHeroContent,
  plansGrid: {
    sectionLabel: 'Membership Programs',
    heading: 'Protection Plans Built for Your Home',
    description: 'Every membership includes priority service, real savings, and peace of mind. Choose the plan that fits your home and budget.',
    quote: 'Honest pricing, lifetime guarantees, and service that treats your home like our own.',
    quoteLabel: 'The Best Neighbor Promise',
    statsJobsCompleted: '500+',
    statsSatisfaction: '99%',
    statsGoogleRating: GOOGLE_RATING,
    plans: [
      {
        name: 'Bargain',
        monthlyPrice: '$10.99',
        monthlyOriginal: '$13.18',
        annualPrice: '$8.79',
        annualOriginal: '$10.99',
        annualTotal: '$105.50',
        priceNote: '',
        badge: '',
        features: ['Never a service fee', 'No after-hours fee', '1 In-person visit per year', 'Earn Replacement Credits!', '$200 Sign-Up Bonus'],
      },
      {
        name: 'Easy',
        monthlyPrice: '$13.49',
        monthlyOriginal: '$16.18',
        annualPrice: '$10.79',
        annualOriginal: '$13.49',
        annualTotal: '$129.50',
        priceNote: '',
        badge: '',
        features: ['Never a service fee', 'No after-hours fee', '1 In-person visit per year', 'Live Chat with Tech', 'Discounted filter replacements', '15% Discount on Services', 'Filter Credit', 'Earn Replacement Credits!', '$350 Sign-Up Bonus'],
      },
      {
        name: 'Signature',
        monthlyPrice: '$17.99',
        monthlyOriginal: '$21.58',
        annualPrice: '$14.39',
        annualOriginal: '$17.99',
        annualTotal: '$172.70',
        priceNote: '',
        badge: 'Most Popular',
        features: ['Never a service fee', 'No after-hours fee', '2 In-person visits per year', 'Live Chat with Tech', 'Discounted filter replacements', '15% Discount on Services', 'Filter Credit', 'Earn Replacement Credits!', '$500 Sign-Up Bonus'],
      },
      {
        name: 'Total',
        monthlyPrice: '$19.99',
        monthlyOriginal: '$23.98',
        annualPrice: '$15.99',
        annualOriginal: '$19.99',
        annualTotal: '$191.90',
        priceNote: '',
        badge: '',
        features: ['Never a service fee', 'No after-hours fee', '1 lb 410a annually', '1 Minor Repair Annually', '2 In-person visits per year', 'Live Chat with Tech', 'Discounted filter replacements', '15% Discount on Services', 'Filter Credit', 'Earn Replacement Credits!', '$750 Sign-Up Bonus'],
      },
      {
        name: 'Filters',
        monthlyPrice: '$45.00',
        monthlyOriginal: '',
        annualPrice: '$45.00',
        annualOriginal: '',
        annualTotal: '$540.00',
        priceNote: 'Starting at $45.00 a filter',
        badge: '',
        features: ['Never a service fee', 'No after-hours fee', '2-4 In-person visits per year', 'Satisfies Warranty Requirements for maintenance'],
      },
    ],
    legacyPlan: {
      name: 'Legacy Plan',
      price: '$759.99',
      priceNote: 'One-time payment for 5 years',
      savings: '$809.40',
      badge: 'Best Value',
      description: "Lock in today's price and enjoy complete HVAC protection for 5 full years. Our most comprehensive plan with the biggest savings.",
      features: ['All Total Plan Benefits', 'Price Locked for 5 Years', 'Zero After-Hours Rates', '$750 Sign-Up Bonus', '$18 Monthly Credit', '2 In-Person Inspections', '$30 Annual Filter Voucher', 'Priority Emergency Service'],
      stats: [
        { value: '5 Years', label: 'Price Locked' },
        { value: '$809+', label: 'Total Savings' },
        { value: '24/7', label: 'Priority Service' },
      ],
    },
  } as PlansGridContent,
  plansCta: {
    pricingHeading: 'Concerned About Pricing?',
    pricingDescription: 'No-Obligation Consultation. Same-Day Appointments. Transparent Pricing. Smart Technology Solutions.',
    pricingButtonLabel: 'Get Your Free Estimate',
    savingsHeading: 'Calculate Your Savings',
    savingsDescription: 'We can help calculate your Membership Savings. See how much you will save on service calls and repairs.',
  } as PlansCTAContent,
  financingHero: {
    title: 'Flexible HVAC',
    titleAccent: 'Financing',
    titleSuffix: 'Options',
    description: 'Affordable HVAC financing through our OPTIMUS partnership. Pre-qualify in under two minutes with no impact to your credit score. Multiple programs available for all credit types.',
  } as FinancingHeroContent,
  financingOverview: {
    sectionLabel: 'OPTIMUS Financing Partner',
    heading: 'Affordable Financing for Every Homeowner',
    description1: "Unexpected HVAC repairs or replacements can be stressful and costly. That's why we partner with OPTIMUS, the all-in-one financing platform that helps us make your project stress-free and affordable.",
    description2: 'OPTIMUS offers multiple financing programs to get you the best rate possible, even if you have challenged credit. Get pre-qualified today for a flexible payment plan in less than two minutes without impacting your credit score.',
    highlights: [
      'Pre-Qualify Without Impacting Credit',
      'Instant Approvals',
      'Affordable Payment Options',
      'No Pre-Payment Penalties',
      'Financing for All Credit Scores',
      'Financing for Repairs and Replacements',
      'Homeownership Not Required',
    ],
    stats: [
      { value: '>90%', label: 'Approval Rate' },
      { value: '0%', label: 'APR Available' },
      { value: '$91', label: 'Payments From' },
      { value: '180', label: 'Month Terms' },
    ],
    promiseHeading: 'The Best Neighbor Promise',
    promiseText: 'No hidden fees, no pre-payment penalties. Just honest financing from your neighborhood HVAC team.',
  } as FinancingOverviewContent,
  financingHowItWorks: {
    sectionLabel: 'Simple Process',
    heading: 'How Financing Works',
    description: 'Four simple steps to affordable comfort for your home. Powered by OPTIMUS -- no hassle, no hidden fees.',
    buttonLabel: 'Start Your Application',
    steps: [
      {
        title: 'Apply Online',
        description: 'Get pre-qualified in less than two minutes through our OPTIMUS financing portal with no impact to your credit score.',
        details: "Our streamlined application connects directly with the OPTIMUS financing platform. You'll need basic personal and financial information. OPTIMUS offers multiple financing programs to find you the best rate possible, even if you have challenged credit.",
        ctaLabel: 'Start Application',
        ctaLink: 'https://apply.optimusfinancing.com/EGIA-488076',
      },
      {
        title: 'Get Approved',
        description: 'Receive instant approval decisions. OPTIMUS works to find you the best financing program available.',
        details: "OPTIMUS offers competitive rates and flexible terms through multiple financing programs. Once approved, you'll see your available options, monthly payment plans, and promotional offers. No hidden fees, no pre-payment penalties.",
        ctaLabel: 'Check Your Options',
        ctaLink: 'https://apply.optimusfinancing.com/EGIA-488076',
      },
      {
        title: 'Schedule Service',
        description: 'Choose your installation date and our veteran-led team handles the rest with precision.',
        details: 'Once approved, our team will work with you to find the best installation date. We handle all permits, equipment ordering, and coordination. Our veteran-led crews arrive on time, protect your home, and complete most installations in a single day.',
        ctaLabel: 'Call to Schedule',
        ctaLink: '',
      },
      {
        title: 'Enjoy Low Payments',
        description: 'Start with payments as low as $91/mo. Many customers offset costs with energy savings.',
        details: 'With flexible financing, you can upgrade to a high-efficiency system that reduces your monthly energy bills. Many homeowners find their energy savings significantly offset their monthly payment, making a new system more affordable than keeping an old, inefficient one.',
        ctaLabel: 'Get a Free Quote',
        ctaLink: '/contact',
      },
    ],
  } as FinancingHowItWorksContent,
  financingPlans: {
    sectionLabel: 'Choose Your Plan',
    heading: 'Financing Plans',
    description: 'Select the option that works best for your budget. All plans include no pre-payment penalties.',
    ctaHeading: 'Ready to Get Started?',
    ctaDescription: 'Get a free estimate and explore your financing options. No obligation, no pressure.',
    ctaButtonLabel: 'Start Application',
    specials: [
      {
        label: 'Most Popular',
        name: '0% APR for 12-72 Months',
        description: 'On new HVAC projects over $3,500',
        monthly: '$91',
        isRate: false,
        highlighted: true,
        features: ['No Down Payment Required', 'Quick Credit Decision', 'Easy Online Application'],
        applyUrl: 'https://apply.optimusfinancing.com/EGIA-488076',
      },
      {
        label: 'Extended Terms',
        name: 'Low Monthly Payments',
        description: 'Spread your payments over up to 15 years with financing that fits your budget.',
        monthly: '7.9% APR',
        isRate: true,
        highlighted: false,
        features: ['Affordable Monthly Payments', 'Terms up to 180 Months', 'Quick Application Process'],
        applyUrl: 'https://apply.optimusfinancing.com/EGIA-488076',
      },
      {
        label: 'High Approval',
        name: 'Over 90% Approval Rate',
        description: 'Multiple financing programs through OPTIMUS and Wells Fargo for all credit types.',
        monthly: '>90%',
        isRate: true,
        highlighted: false,
        features: ['Programs for All Credit Types', 'Competitive Rates', 'Fast Funding'],
        applyUrl: 'https://apply.optimusfinancing.com/EGIA-488076',
      },
    ],
  } as FinancingPlansContent,
  financingBenefits: {
    sectionLabel: 'Why Finance With Us',
    heading: 'Smart Financing Benefits',
    description: 'Powered by OPTIMUS and Wells Fargo. Many customers find their monthly energy savings offset their payment when upgrading to a new, efficient system.',
    benefits: [
      { title: 'Quick Approval', description: 'Same-day decisions and fast funding for your HVAC needs.', items: ['Same-day credit decisions', 'Fast funding for approved projects', 'No down payment required'] },
      { title: 'Flexible Options', description: 'Multiple terms and payment plans to fit your budget.', items: ['0% APR for 12-72 months', 'Terms up to 180 months', 'Financing up to $50,000'] },
      { title: 'Trusted Partners', description: 'Secured financing through OPTIMUS and Wells Fargo.', items: ['OPTIMUS all-in-one financing platform', 'Wells Fargo promotional financing', 'Secure, transparent loan terms'] },
      { title: 'Easy Process', description: 'Simple online application with quick results.', items: ['Pre-qualify in under two minutes', 'No impact to your credit score', 'Apply and manage payments digitally'] },
    ],
  } as FinancingBenefitsContent,
  serviceLocationHero: {
    title: 'Areas',
    titleAccent: 'We Serve',
    titleSuffix: 'in North Texas',
    description: 'From Dallas to Sherman and everywhere in between. 24/7 emergency HVAC services across 4 counties and 37+ cities in North Texas.',
  } as ServiceLocationHeroContent,
  serviceLocationMap: {
    directoryHeading: 'City Directory',
    directoryDescription: 'Browse all cities we serve across 4 counties. Click any city to view it on the map.',
    ctaBadge: 'Coverage Inquiry',
    ctaHeading: 'Do Not See Your City Listed?',
    ctaDescription: 'Our coverage extends beyond what is shown here. We serve many additional communities throughout North Texas. Give us a call to confirm service in your area.',
    ctaCallbackLabel: 'Request a Callback',
    services: ['AC Installation & Repair', 'Heating Services', '24/7 Emergency Service'],
  } as ServiceLocationMapContent,
  contactHero: {
    title: 'Get In',
    titleAccent: 'Contact',
    titleSuffix: 'With Us Today',
    description: "Ready for reliable comfort? Get a free, no-obligation estimate from North Texas's trusted veteran-owned HVAC team. Same-day appointments available.",
  } as ContactHeroContent,
  contactInfo: {
    sectionLabel: 'Contact Us',
    heading: 'We Are Here to Help',
    description: 'Whether you need emergency service, want to schedule maintenance, or have questions about our plans, our team is ready to assist.',
    phoneSubtext: 'Call us anytime 24/7',
    emailSubtext: 'Email us anytime',
    address: '414 Metro Park Dr Suite 201',
    addressSubtext: 'McKinney, TX 75071',
    hours: 'Monday - Sunday: 24/7',
    hoursSubtext: 'Including weekends and holidays',
    stats: [
      { value: '500+', label: 'Jobs Completed' },
      { value: '99%', label: 'Satisfaction' },
      { value: GOOGLE_RATING, label: 'Google Rating' },
    ],
    formBadge: 'Same-Day Service Available',
    formHeading: 'Request Your Free Estimate',
    formDescription: 'Fill out the form below and we will contact you within 24 hours.',
    formDisclaimer: 'No obligation. No pressure. Just honest service from your neighborhood HVAC team. We typically respond within 2 hours during business hours.',
    formButtonLabel: 'Send Your Request',
    thankYouHeading: 'Thank You!',
    thankYouDescription: 'We have received your message and will get back to you within 24 hours. For immediate assistance, please call us directly.',
    serviceOptions: [
      { value: 'ac-install', label: 'AC Installation' },
      { value: 'ac-repair', label: 'AC Repair' },
      { value: 'heating', label: 'Heating Services' },
      { value: 'heat-pump', label: 'Heat Pump Solutions' },
      { value: 'mini-split', label: 'Mini Split Systems' },
      { value: 'maintenance', label: 'Preventive Maintenance' },
      { value: 'emergency', label: 'Emergency Service' },
      { value: 'other', label: 'Other' },
    ],
  } as ContactInfoContent,
  seoGlobal: {
    siteName: 'Best Neighbor Home Services',
    titleTemplate: '%s | Best Neighbor Home Services',
    defaultOgImage: '/og-image.jpg',
    googleVerification: '',
    bingVerification: '',
    locale: 'en_US',
    twitterHandle: '',
    facebookUrl: '',
    businessType: 'HVACBusiness',
    businessName: 'Best Neighbor Home Services',
    businessDescription: 'Veteran-owned HVAC company serving North Texas with AC installation, heating repair, heat pumps, mini splits, and 24/7 emergency service.',
    streetAddress: '414 Metro Park Dr Suite 201',
    city: 'McKinney',
    state: 'TX',
    zipCode: '75071',
    country: 'US',
    latitude: '33.1972',
    longitude: '-96.6397',
    priceRange: '$$',
    openingHours: ['Mo-Su 00:00-23:59'],
    sameAs: ['https://www.google.com/search?q=Best+Neighbor+Home+Services+reviews'],
  } as SEOGlobalContent,
  seoHome: {
    metaTitle: 'Best Neighbor Home Services | North Texas HVAC Experts',
    metaDescription: 'Veteran-owned HVAC services in McKinney and North Texas. Get 24/7 AC and heating repair, expert installation, and free estimates. Call (903) 321-2265.',
    keywords: 'HVAC McKinney TX, AC repair North Texas, heating services, veteran-owned HVAC, emergency AC repair, air conditioning installation, furnace repair',
    ogTitle: 'Best Neighbor Home Services | Your Trusted HVAC Partner',
    ogDescription: 'Veteran-owned and operated. Military-grade precision and neighborly care for every heating and cooling service in North Texas.',
    ogImage: '',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    canonicalUrl: '/',
    robots: 'index, follow',
    jsonLdType: 'LocalBusiness',
  } as SEOPageContent,
  seoPlans: {
    metaTitle: 'HVAC Membership Plans | Best Neighbor Home Services',
    metaDescription: 'HVAC membership plans from $10.99/month with priority scheduling, maintenance, and member savings. Find the right protection for your North Texas home.',
    keywords: 'HVAC membership plans, AC maintenance plan, heating maintenance McKinney, HVAC service agreement, preventive maintenance plan Texas',
    ogTitle: 'HVAC Membership Plans | Protection Plans Built for Your Home',
    ogDescription: 'Every membership includes priority service, real savings, and peace of mind. Choose the plan that fits your home and budget.',
    ogImage: '',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    canonicalUrl: '/plans',
    robots: 'index, follow',
    jsonLdType: 'Product',
  } as SEOPageContent,
  seoFinancing: {
    metaTitle: 'HVAC Financing Options | 0% APR Available | Best Neighbor',
    metaDescription: 'Flexible HVAC financing through OPTIMUS. 0% APR for 12-72 months, over 90% approval rate, no down payment. Pre-qualify in under 2 minutes with no credit impact.',
    keywords: 'HVAC financing, AC financing Texas, 0% APR HVAC, air conditioning payment plans, furnace financing options McKinney',
    ogTitle: 'Flexible HVAC Financing Options | Best Neighbor Home Services',
    ogDescription: 'Affordable HVAC financing through our OPTIMUS partnership. Pre-qualify in under two minutes with no impact to your credit score.',
    ogImage: '',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    canonicalUrl: '/financing',
    robots: 'index, follow',
    jsonLdType: 'FinancialProduct',
  } as SEOPageContent,
  seoServiceAreas: {
    metaTitle: 'Service Areas | HVAC in McKinney, Allen, Frisco & North Texas',
    metaDescription: 'Serving 37+ cities across Collin, Grayson, Hunt counties & Dallas area. 24/7 emergency HVAC services in McKinney, Allen, Frisco, Plano, Sherman & more.',
    keywords: 'HVAC McKinney, AC repair Allen TX, Frisco HVAC, Plano air conditioning, Sherman heating repair, North Texas HVAC service areas',
    ogTitle: 'Service Areas | Best Neighbor Home Services',
    ogDescription: 'From Dallas to Sherman and everywhere in between. 24/7 emergency HVAC services across 4 counties and 37+ cities in North Texas.',
    ogImage: '',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    canonicalUrl: '/service-areas',
    robots: 'index, follow',
    jsonLdType: 'ServiceArea',
  } as SEOPageContent,
  seoContact: {
    metaTitle: 'Contact Us | Free HVAC Estimate | Best Neighbor Home Services',
    metaDescription: 'Get a free, no-obligation HVAC estimate. Same-day appointments available. Call (903) 321-2265 or fill out our online form. Veteran-owned, serving North Texas 24/7.',
    keywords: 'contact HVAC McKinney, free AC estimate North Texas, HVAC quote, schedule HVAC service, emergency AC repair phone number',
    ogTitle: 'Contact Best Neighbor Home Services | Get a Free Estimate',
    ogDescription: 'Ready for reliable comfort? Get a free, no-obligation estimate from North Texas\'s trusted veteran-owned HVAC team.',
    ogImage: '',
    ogType: 'website',
    twitterCard: 'summary_large_image',
    canonicalUrl: '/contact',
    robots: 'index, follow',
    jsonLdType: 'ContactPage',
  } as SEOPageContent,
};
